import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import MenuButton from "../MenuButton/MenuButton";
import { NoteState } from "../../context/notes/NoteState";
import UserServices from "../../UserServices/UserServices";
import MISReportPrintDocument from "../MISReport/MISReportPrintDocument";
import { exportMisReportPdf } from "../MISReport/exportMisReportPdf";
import DashboardBranchFilter, {
  useDashboardBranch,
} from "../Includes/DashboardBranchFilter";
import { ReminderBanner } from "../AdminReminders/AdminReminders";
import "./AdminDashboard.css";

const BRAND = "#e8002b";
const TEAL = "#0f766e";
const AMBER = "#d97706";
const BLUE = "#2563eb";
const PIE = [BRAND, TEAL, AMBER, BLUE, "#7c3aed", "#059669", "#64748b", "#db2777"];

const money = (v) =>
  `Rs ${Number(v || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 })}`;
const num = (v) => Number(v || 0).toLocaleString("en-PK");

const Metric = ({ label, value }) => (
  <div className="ad-metric">
    <span className="ad-metric-label">{label}</span>
    <strong className="ad-metric-value">{value}</strong>
  </div>
);

const Kpi = ({ label, value, sub, tone = "slate", icon }) => (
  <div className={`ad-kpi ad-tone-${tone}`}>
    <div className="ad-kpi-body">
      <p className="ad-kpi-label">{label}</p>
      <h4 className="ad-kpi-value" title={typeof value === "string" ? value : undefined}>
        {value}
      </h4>
      {sub ? <p className="ad-kpi-sub">{sub}</p> : null}
    </div>
    <div className="ad-kpi-icon">
      <i className={`bi ${icon}`}></i>
    </div>
  </div>
);

export default function AdminDashboard() {
  const { state } = useContext(NoteState);
  const { first_name, role, branch_id } = state;
  const { branch, setBranch } = useDashboardBranch(role, branch_id);
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const [misPdf, setMisPdf] = useState(null);
  const printRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await UserServices.adminDashboardSummary(branch, {
        timeout: 25000,
        date,
      });
      const payload =
        res?.data?.data && typeof res.data.data === "object" ? res.data.data : res?.data;
      if (res.status === 200 && payload) {
        setData(payload);
      } else {
        setData(null);
        setError("No dashboard data for this branch.");
      }
    } catch (err) {
      console.error(err);
      setData(null);
      setError(
        err.code === "ECONNABORTED"
          ? "Dashboard is taking too long. Try again."
          : "Could not load Admin Dashboard."
      );
    } finally {
      setLoading(false);
    }
  }, [branch, date]);

  useEffect(() => {
    load();
  }, [load]);

  const breakupChart = useMemo(() => {
    const b = data?.breakup || {};
    return [
      { name: "Gym New", net: Number(b.gym_new?.net || 0) },
      { name: "Gym Renew", net: Number(b.gym_renew?.net || 0) },
      { name: "PT New", net: Number(b.pt_new?.net || 0) },
      { name: "PT Renew", net: Number(b.pt_renew?.net || 0) },
      { name: "GX", net: Number(b.gx?.net || 0) },
      { name: "Nutrition", net: Number(b.nutrition?.net || 0) },
      { name: "Physio", net: Number(b.physio?.net || 0) },
      { name: "Academy", net: Number(b.academy?.net || 0) },
      { name: "Cafe", net: Number(b.cafe?.net || 0) },
      { name: "Other", net: Number(b.other?.net || 0) },
    ].filter((r) => r.net > 0);
  }, [data]);

  const footfallPie = useMemo(
    () => [
      { name: "Morning", value: Number(data?.morning || 0) },
      { name: "Afternoon", value: Number(data?.afternoon || 0) },
      { name: "Evening", value: Number(data?.evening || 0) },
    ],
    [data]
  );

  const staffPie = useMemo(
    () => [
      { name: "Present", value: Number(data?.presentStaff || 0) },
      { name: "Absent", value: Number(data?.absentStaff || 0) },
      { name: "Late", value: Number(data?.lateStaff || 0) },
      { name: "Leave", value: Number(data?.leaveCount || 0) },
    ].filter((r) => r.value > 0),
    [data]
  );

  const dept = data?.dept_snapshot || {};

  const trend = data?.trend || [];
  const salesToday = Number(
    data?.total_sales_today ?? Number(data?.salenet_today || 0) + Number(data?.csalenet_today || 0)
  );
  const salesMtd = Number(
    data?.total_sales_mtd ?? Number(data?.salem_net || 0) + Number(data?.csalem_net || 0)
  );

  const absentLate = data?.absentLateStaff || [];
  const roster = data?.staffRoster || [];
  const studioSlots = data?.studioSlots || [];

  const downloadMis = async () => {
    setDownloading(true);
    setError("");
    try {
      const res = await UserServices.MISReport(branch, { date, timeout: 45000 });
      const payload = res?.data?.data ?? res?.data;
      if (!payload?.meta) {
        setError("Could not load MIS report.");
        return;
      }
      setMisPdf(payload);
      let root = null;
      for (let i = 0; i < 20; i += 1) {
        await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 50)));
        root = printRef.current?.querySelector(".mis-print-root");
        if (root) break;
      }
      if (!root) {
        setError("PDF layout not ready. Try again.");
        return;
      }
      await exportMisReportPdf(root, { branch, date });
    } catch (err) {
      console.error(err);
      setError("Could not download MIS report.");
    } finally {
      setDownloading(false);
    }
  };

  const branchLabel =
    data?.branch_label ||
    (branch === "1" ? "G-13" : branch === "15" ? "F-11" : "All Branches");
  const showBranchCol = Boolean(data?.is_all_branches);

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design ad-shell">
          <MenuButton />
          <div className="container-fluid px-3 px-md-4 pb-4">
            <ReminderBanner />
            <div className="ad-topbar">
              <div>
                <p className="ad-eyebrow">Super Admin</p>
                <h2 className="ad-title">
                  <i className="bi bi-speedometer2 me-2"></i>
                  Admin Dashboard
                </h2>
                <p className="ad-subtitle">
                  {moment(data?.date || date).format("dddd, DD MMM YYYY")}
                  {first_name ? ` · Welcome ${first_name}` : ""}
                </p>
              </div>
              <div className="ad-toolbar">
                <DashboardBranchFilter
                  role={role}
                  loginBranchId={branch_id}
                  value={branch}
                  onChange={setBranch}
                />
                <input
                  type="date"
                  className="form-control"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  aria-label="Date"
                />
                <button type="button" className="btn btn-outline-danger" onClick={load} disabled={loading}>
                  {loading ? "Loading…" : "Refresh"}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={downloadMis}
                  disabled={downloading}
                >
                  {downloading ? "Building PDF…" : "Download MIS"}
                </button>
                <Link to="/mis-report" className="btn btn-outline-secondary">
                  Open MIS
                </Link>
              </div>
            </div>

            {error ? <div className="alert alert-warning py-2">{error}</div> : null}

            {loading && !data ? (
              <div className="text-center py-5">
                <div className="spinner-border text-danger" role="status" />
                <div className="text-muted mt-2 small">Loading dashboard…</div>
              </div>
            ) : (
              <>
                <h3 className="ad-section-title">Today at a glance · {branchLabel}</h3>
                <div className="ad-kpi-grid">
                  <Kpi
                    label="Today Sales"
                    value={money(salesToday)}
                    sub={`Membership ${money(data?.salenet_today)} · Cafe ${money(data?.csalenet_today)}`}
                    icon="bi-cash-stack"
                    tone="red"
                  />
                  <Kpi
                    label="Month Sales"
                    value={money(salesMtd)}
                    sub={`Qty ${num(data?.salem_qty)} · Cafe ${money(data?.csalem_net)}`}
                    icon="bi-graph-up-arrow"
                    tone="slate"
                  />
                  <Kpi
                    label="Today Expenses"
                    value={money(data?.today_expense)}
                    sub={`MTD ${money(data?.t_expense_date)} · Pending ${num(data?.pending_expense_approvals)}`}
                    icon="bi-receipt"
                    tone="amber"
                  />
                  <Kpi
                    label="Physio"
                    value={money(dept?.physio?.sales_net)}
                    sub={`Appts ${num(dept?.physio?.appointments_today)} · MTD ${money(dept?.physio?.mtd_net)}`}
                    icon="bi-heart-pulse"
                    tone="slate"
                  />
                  <Kpi
                    label="Nutrition"
                    value={money(dept?.nutrition?.sales_net)}
                    sub={`Appts ${num(dept?.nutrition?.appointments_today)} · MTD ${money(dept?.nutrition?.mtd_net)}`}
                    icon="bi-egg-fried"
                    tone="red"
                  />
                  <Kpi
                    label="Social Leads"
                    value={num(dept?.social_leads?.leads_today)}
                    sub={`Visits ${num(dept?.social_leads?.visit_completed)} · Paid ${num(dept?.social_leads?.payments)} · MTD paid ${num(dept?.social_leads?.mtd_payments)}`}
                    icon="bi-megaphone"
                    tone="slate"
                  />
                  <Kpi
                    label="Staff Present"
                    value={num(data?.presentStaff)}
                    sub={`of ${num(data?.totalStaff)} · Absent ${num(data?.absentStaff)} · Late ${num(data?.lateStaff)}`}
                    icon="bi-person-check"
                    tone="red"
                  />
                  <Kpi
                    label="Footfall"
                    value={num(data?.totalCheckins)}
                    sub={`Male ${num(data?.totalMales)} · Female ${num(data?.totalFemales)} · Absent paid ${num(data?.absentPaidClients)}`}
                    icon="bi-people"
                    tone="slate"
                  />
                </div>

                {showBranchCol && Array.isArray(data?.by_branch) && data.by_branch.length ? (
                  <div className="row g-3 mb-4">
                    {data.by_branch.map((b) => (
                      <div className="col-12 col-md-6" key={b.branch_id}>
                        <div className="ad-panel">
                          <div className="ad-panel-head">
                            <h5>{b.branch_label}</h5>
                            <span className="ad-chip">Branch split</span>
                          </div>
                          <div className="ad-metrics">
                            <Metric label="Today sales" value={money(b.total_sales_today)} />
                            <Metric label="Physio sales" value={money(b.physio_sales)} />
                            <Metric label="Nutrition sales" value={money(b.nutrition_sales)} />
                            <Metric label="Leads today" value={num(b.leads_today)} />
                            <Metric label="Footfall" value={num(b.totalCheckins)} />
                            <Metric label="Staff present" value={num(b.presentStaff)} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}

                <h3 className="ad-section-title">Sales performance</h3>
                <div className="row g-3 mb-4">
                  <div className="col-12 col-xl-7">
                    <div className="ad-panel">
                      <div className="ad-panel-head">
                        <h5>Sales vs Expenses (7 days)</h5>
                        <span className="ad-chip">Incl. cafe</span>
                      </div>
                      <div className="ad-chart">
                        <ResponsiveContainer width="100%" height={280}>
                          <LineChart data={trend} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                            <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748b" }} />
                            <YAxis tick={{ fontSize: 12, fill: "#64748b" }} width={64} />
                            <Tooltip formatter={(v) => money(v)} />
                            <Legend />
                            <Line type="monotone" dataKey="sales" name="Sales" stroke={BRAND} strokeWidth={2.5} dot={false} />
                            <Line type="monotone" dataKey="expenses" name="Expenses" stroke={AMBER} strokeWidth={2} dot={false} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-xl-5">
                    <div className="ad-panel h-100">
                      <div className="ad-panel-head">
                        <h5>Sales by service</h5>
                      </div>
                      <div className="ad-chart">
                        {breakupChart.length ? (
                          <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={breakupChart} margin={{ top: 8, right: 8, left: 0, bottom: 40 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} interval={0} angle={-20} textAnchor="end" height={52} />
                              <YAxis tick={{ fontSize: 12, fill: "#64748b" }} width={64} />
                              <Tooltip formatter={(v) => money(v)} />
                              <Bar dataKey="net" name="Net" radius={[6, 6, 0, 0]}>
                                {breakupChart.map((_, i) => (
                                  <Cell key={i} fill={PIE[i % PIE.length]} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="ad-empty">No sales for this date</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <h3 className="ad-section-title">Operations</h3>
                <div className="row g-3 mb-4">
                  <div className="col-12 col-xl-6">
                    <div className="ad-panel">
                      <div className="ad-panel-head">
                        <h5>Footfall details</h5>
                        <span className="ad-chip">Client check-ins</span>
                      </div>
                      <div className="row g-3">
                        <div className="col-md-5">
                          <ResponsiveContainer width="100%" height={210}>
                            <PieChart>
                              <Pie data={footfallPie} dataKey="value" nameKey="name" innerRadius={48} outerRadius={78} paddingAngle={2}>
                                {footfallPie.map((_, i) => (
                                  <Cell key={i} fill={PIE[i % PIE.length]} />
                                ))}
                              </Pie>
                              <Tooltip />
                              <Legend />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="col-md-7">
                          <div className="ad-metrics">
                            <Metric label="Morning 7–12" value={num(data?.morning)} />
                            <Metric label="Afternoon 12–5" value={num(data?.afternoon)} />
                            <Metric label="Evening 5–11" value={num(data?.evening)} />
                            <Metric label="Total check-ins" value={num(data?.totalCheckins)} />
                            <Metric label="Male / Female" value={`${num(data?.totalMales)} / ${num(data?.totalFemales)}`} />
                            <Metric label="Busiest" value={data?.busiestTimeSlot || "No check-ins"} />
                            <Metric label="Slowest" value={data?.slowestTimeSlot || "No check-ins"} />
                            <Metric label="Active paid members" value={num(data?.activePaidClients)} />
                            <Metric label="Absent paid clients" value={num(data?.absentPaidClients)} />
                            <Metric label="Visitors / walk-ins" value={num(data?.visitorsWalkIns)} />
                            <Metric label="GX studio total" value={num(data?.totalStudioAttendance)} />
                            <Metric
                              label="GX sessions 11 / 05 / 06 / 07"
                              value={`${num(data?.studioAttendanceSession1)} / ${num(data?.studioAttendanceSession2)} / ${num(data?.studioAttendanceSession3)} / ${num(data?.studioAttendanceSession4)}`}
                            />
                          </div>
                          {studioSlots.length ? (
                            <div className="ad-slot-grid">
                              {studioSlots.map((s) => (
                                <div className="ad-slot" key={s.slot}>
                                  <span>{s.slot}</span>
                                  <strong>{s.count}</strong>
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-xl-6">
                    <div className="ad-panel">
                      <div className="ad-panel-head">
                        <h5>Staff details</h5>
                        <span className="ad-chip">
                          PT {num(data?.ptStaffPresent)} · Leave {num(data?.leaveCount)}
                        </span>
                      </div>
                      <div className="row g-3">
                        <div className="col-md-5">
                          <ResponsiveContainer width="100%" height={190}>
                            <PieChart>
                              <Pie data={staffPie} dataKey="value" nameKey="name" innerRadius={48} outerRadius={78}>
                                {staffPie.map((_, i) => (
                                  <Cell key={i} fill={PIE[i % PIE.length]} />
                                ))}
                              </Pie>
                              <Tooltip />
                              <Legend />
                            </PieChart>
                          </ResponsiveContainer>
                          <div className="ad-metrics mt-2">
                            <Metric label="Total staff" value={num(data?.totalStaff)} />
                            <Metric
                              label="Present / Absent / Late"
                              value={`${num(data?.presentStaff)} / ${num(data?.absentStaff)} / ${num(data?.lateStaff)}`}
                            />
                            <Metric label="Pending expense approvals" value={num(data?.pending_expense_approvals)} />
                          </div>
                        </div>
                        <div className="col-md-7">
                          <div className="ad-table-wrap">
                            <table className="ad-table">
                              <thead>
                                <tr>
                                  {showBranchCol ? <th>Branch</th> : null}
                                  <th>Name</th>
                                  <th>Dept</th>
                                  <th>Status</th>
                                  <th>In</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(absentLate.length ? absentLate : roster.slice(0, 12)).map((row, i) => (
                                  <tr key={`${row.branch || ""}-${row.name}-${i}`}>
                                    {showBranchCol ? <td>{row.branch || "-"}</td> : null}
                                    <td>{row.name}</td>
                                    <td>{row.department || "-"}</td>
                                    <td>
                                      <span className={`ad-pill ad-pill-${String(row.status || "").toLowerCase()}`}>
                                        {row.status}
                                      </span>
                                    </td>
                                    <td>{row.checkin || "-"}</td>
                                  </tr>
                                ))}
                                {!absentLate.length && !roster.length ? (
                                  <tr>
                                    <td colSpan={showBranchCol ? 5 : 4} className="text-muted">
                                      No staff attendance for this date
                                    </td>
                                  </tr>
                                ) : null}
                              </tbody>
                            </table>
                          </div>
                          {roster.length > 12 && !absentLate.length ? (
                            <p className="ad-footnote">Showing first 12 of {roster.length} staff</p>
                          ) : null}
                          {absentLate.length ? (
                            <p className="ad-footnote">
                              Showing absent / late / leave ({absentLate.length}). Full roster: {roster.length}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <h3 className="ad-section-title">Departments</h3>
                <div className="row g-3 mb-4">
                  <div className="col-12 col-md-6 col-xl-3">
                    <div className="ad-panel">
                      <div className="ad-panel-head">
                        <h5>Sales</h5>
                        <Link to="/detailed-sales-report" className="ad-chip">
                          Details
                        </Link>
                      </div>
                      <div className="ad-metrics">
                        <Metric label="Gym today" value={`${money(dept?.sales?.gym_net)} · ${num(dept?.sales?.gym_qty)}`} />
                        <Metric label="PT today" value={`${money(dept?.sales?.pt_net)} · ${num(dept?.sales?.pt_qty)}`} />
                        <Metric label="GX today" value={`${money(dept?.sales?.gx_net)} · ${num(dept?.sales?.gx_qty)}`} />
                        <Metric label="Cafe today" value={money(dept?.sales?.cafe_net)} />
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-md-6 col-xl-3">
                    <div className="ad-panel">
                      <div className="ad-panel-head">
                        <h5>Physio</h5>
                        <Link to="/physio/dashboard" className="ad-chip">
                          Details
                        </Link>
                      </div>
                      <div className="ad-metrics">
                        <Metric label="Appointments today" value={num(dept?.physio?.appointments_today)} />
                        <Metric label="Sales qty" value={num(dept?.physio?.sales_qty)} />
                        <Metric label="Sales net" value={money(dept?.physio?.sales_net)} />
                        <Metric label="MTD sales" value={money(dept?.physio?.mtd_net)} />
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-md-6 col-xl-3">
                    <div className="ad-panel">
                      <div className="ad-panel-head">
                        <h5>Nutrition</h5>
                        <Link to="/nutrition/dashboard" className="ad-chip">
                          Details
                        </Link>
                      </div>
                      <div className="ad-metrics">
                        <Metric label="Appointments today" value={num(dept?.nutrition?.appointments_today)} />
                        <Metric label="Sales qty" value={num(dept?.nutrition?.sales_qty)} />
                        <Metric label="Sales net" value={money(dept?.nutrition?.sales_net)} />
                        <Metric label="MTD sales" value={money(dept?.nutrition?.mtd_net)} />
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-md-6 col-xl-3">
                    <div className="ad-panel">
                      <div className="ad-panel-head">
                        <h5>Social Leads</h5>
                        <Link to="/social-leads" className="ad-chip">
                          Details
                        </Link>
                      </div>
                      <div className="ad-metrics">
                        <Metric label="Leads today" value={num(dept?.social_leads?.leads_today)} />
                        <Metric label="Interested" value={num(dept?.social_leads?.interested)} />
                        <Metric
                          label="Visit scheduled / done"
                          value={`${num(dept?.social_leads?.visit_scheduled)} / ${num(dept?.social_leads?.visit_completed)}`}
                        />
                        <Metric
                          label="Paid today / MTD"
                          value={`${num(dept?.social_leads?.payments)} / ${num(dept?.social_leads?.mtd_payments)}`}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <h3 className="ad-section-title">Snapshots</h3>
                <div className="row g-3 mb-4">
                  <div className="col-12 col-md-6 col-xl-4">
                    <div className="ad-panel">
                      <div className="ad-panel-head">
                        <h5>Cafe</h5>
                      </div>
                      <div className="ad-metrics">
                        <Metric label="Net sales" value={money(data?.csalenet_today)} />
                        <Metric label="Quantity" value={num(data?.csaleqty_today)} />
                        <Metric
                          label="Meals / Drinks / Sides"
                          value={`${num(data?.cafe_meals)} / ${num(data?.cafe_drinks)} / ${num(data?.cafe_sides)}`}
                        />
                        <Metric label="Staff orders" value={num(data?.cafe_staff_orders)} />
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-md-6 col-xl-4">
                    <div className="ad-panel">
                      <div className="ad-panel-head">
                        <h5>Membership</h5>
                      </div>
                      <div className="ad-metrics">
                        <Metric
                          label="Gym new / renew"
                          value={`${money(data?.breakup?.gym_new?.net)} / ${money(data?.breakup?.gym_renew?.net)}`}
                        />
                        <Metric
                          label="PT new / renew"
                          value={`${money(data?.breakup?.pt_new?.net)} / ${money(data?.breakup?.pt_renew?.net)}`}
                        />
                        <Metric label="GX" value={money(data?.breakup?.gx?.net)} />
                        <Metric label="Nutrition" value={money(data?.breakup?.nutrition?.net)} />
                        <Metric label="Physio" value={money(data?.breakup?.physio?.net)} />
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-xl-4">
                    <div className="ad-panel">
                      <div className="ad-panel-head">
                        <h5>Quick links</h5>
                      </div>
                      <div className="ad-quick-links">
                        <Link to="/detailed-sales-report">
                          <i className="bi bi-bar-chart-line"></i> Sales Report
                        </Link>
                        <Link to="/hr-dashboard">
                          <i className="bi bi-people"></i> HR
                        </Link>
                        <Link to="/view-expenses">
                          <i className="bi bi-wallet2"></i> Expenses
                        </Link>
                        <Link to="/mis-report">
                          <i className="bi bi-file-earmark-text"></i> Full MIS
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>

              </>
            )}
            {misPdf ? (
              <div ref={printRef}>
                <MISReportPrintDocument data={misPdf} />
              </div>
            ) : null}
          </div>
        </div>
      </main>
    </div>
  );
}
